import { replaceNodes } from "@shared/unified-tiptap/util/descendant-helpers"
import type { Editor } from "@tiptap/core"
import type { ImageOptions } from "@tiptap/extension-image"
import { Image as TiptapImage } from "@tiptap/extension-image"
import { Slice } from "@tiptap/pm/model"
import { Plugin } from "@tiptap/pm/state"
import { ReactNodeViewRenderer } from "@tiptap/react"
import type * as filestack from "filestack-js"
import { reportOperation } from "../../analytics/operation-event"
import { OPERATION_CONTRACTS } from "../../analytics/operations"
import { asImageId, type ImageId } from "../../analytics/types"
import { insertBlockContent } from "../../util/insert-block-content"
import { ImageNodeView } from "./image-node-view"
import { nextImageUploadId, watchImageUpload } from "./image-upload"

export interface ImageExtensionOptions extends ImageOptions {
  filestackClient: filestack.Client | null
}

interface ImageAttributes {
  src: string | null
  alt?: string | null
  title?: string | null
  width?: string | null
  height?: string | null
  "data-align"?: string | null
  uploadId?: number | null
}

const parseImageAttributes = (img: Element): Partial<ImageAttributes> => ({
  src: img.getAttribute("src"),
  alt: img.getAttribute("alt"),
  title: img.getAttribute("title"),
  width: img.getAttribute("width"),
  height: img.getAttribute("height"),
})

function buildImageHTMLAttributes(attrs: ImageAttributes): Record<string, string> {
  const result: Record<string, string> = { src: attrs.src || "" }

  if (attrs.alt) result.alt = attrs.alt
  if (attrs.title) result.title = attrs.title
  if (attrs.width) result.width = attrs.width
  if (attrs.height) result.height = attrs.height

  return result
}

/**
 * Fork of Tiptap UI's Image extension.
 *
 * Tiptap UI adds a NodeView with resizing handles. We further add the drag pad,
 * image uploading, and our widget spec, and we remove unused Tiptap UI features (captions).
 */
export const Image = TiptapImage.extend<ImageExtensionOptions>({
  group: "block widget",

  addOptions() {
    return {
      ...this.parent?.(),
      inline: false,
      // Base64 is allowed so that Tiptap can parse pasted images. They're converted to
      // an uploading placeholder before reaching the editor state - see the extension's
      // transformPasted prop.
      allowBase64: true,
      filestackClient: null,
    } as ImageExtensionOptions
  },

  addAttributes() {
    return {
      ...this.parent?.(),
      "data-align": {
        default: null,
      },
      // Set while an image is uploading, in place of src. Not persisted to HTML:
      // an upload can't be resumed after a reload, so it has no meaning outside
      // the live editor session.
      uploadId: {
        default: null,
        rendered: false,
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: "figure",
        getAttrs: (node) => {
          const img = node.querySelector("img")
          if (!img) return false

          return {
            ...parseImageAttributes(img),
            "data-align": node.getAttribute("data-align"),
          }
        },
      },
      {
        tag: "img[src]",
        getAttrs: (node) => {
          if (node.closest("figure")) return false

          return {
            ...parseImageAttributes(node),
            "data-align": node.getAttribute("data-align"),
          }
        },
      },
    ]
  },

  renderHTML({ node }) {
    const { src, alt, title, width, height } = node.attrs
    const align = node.attrs["data-align"]

    const imgAttrs = buildImageHTMLAttributes({ src, alt, title, width, height })
    if (align) imgAttrs["data-align"] = align

    return ["img", imgAttrs]
  },

  addNodeView() {
    return ReactNodeViewRenderer(ImageNodeView)
  },

  /**
   * Uploads dropped/pasted images to Filestack, and replaces base64 (`data:image`) images
   * pasted as inline HTML (e.g. from Word/Google Docs) with the same uploading
   * placeholder. In both cases the image node is inserted immediately with `uploadId` set
   * (rendering "Uploading..." — see image-node-view.tsx), then patched in place with the
   * real `src` once the Filestack upload resolves (image-upload.ts's watchImageUpload).
   */
  addProseMirrorPlugins() {
    const editor = this.editor
    const nodeName = this.name
    const { filestackClient } = this.options

    const onlyImageFiles = (fileList: FileList): File[] =>
      [...fileList].filter((file) => file.type.startsWith("image/"))

    function uploadFiles(files: File[], pos?: number): void {
      if (files.length === 0 || !filestackClient) return
      const content = files.map((file) => {
        const uploadId = nextImageUploadId()
        watchImageUpload(editor, filestackClient, uploadId, file)
        return { type: nodeName, attrs: { uploadId } }
      })
      insertBlockContent(editor, content, pos)
    }

    return [
      new Plugin({
        props: {
          handleDrop(view, event) {
            if (!editor.isEditable || !event.dataTransfer) return false

            const files = onlyImageFiles(event.dataTransfer.files)
            if (files.length === 0) return false

            event.preventDefault()
            const pos = view.posAtCoords({ left: event.clientX, top: event.clientY })?.pos
            uploadFiles(files, pos)

            // Return false so that the FileUpload handler can also run.
            return false
          },

          handlePaste(_view, event) {
            if (!editor.isEditable || !event.clipboardData) return false

            const files = onlyImageFiles(event.clipboardData.files)
            if (files.length === 0) return false

            event.preventDefault()
            uploadFiles(files)

            // Return false so that the FileUpload handler can also run.
            return false
          },

          transformPasted(slice) {
            if (!editor.isEditable || !filestackClient) return slice

            return transformPastedImages(editor, filestackClient, nodeName, slice)
          },
        },
      }),
    ]
  },
})

/**
 * Replaces base64 (`data:image`) images in a pasted slice with an uploading placeholder,
 * and strips `file://` images (seen from Word paste): they're local paths that don't make
 * sense in the app, and have been observed to crash the print service.
 */
function transformPastedImages(
  editor: Editor,
  client: filestack.Client,
  nodeName: string,
  slice: Slice,
): Slice {
  const uploadIds: ImageId[] = []
  const newContent = replaceNodes(slice.content, (node) => {
    if (node.type.name !== nodeName) return node
    const src: string | null = node.attrs.src
    if (src === null) return node

    if (src.startsWith("data:image")) {
      const uploadId = nextImageUploadId()
      uploadIds.push(asImageId(String(uploadId)))
      watchImageUpload(editor, client, uploadId, src)
      return node.type.create({ ...node.attrs, src: null, uploadId }, node.content, node.marks)
    }
    if (src.startsWith("file://")) return null

    return node
  })

  const [first, ...rest] = uploadIds
  if (first !== undefined) {
    reportOperation(editor, OPERATION_CONTRACTS.OP_INSERT_IMAGE, "clipboard", {
      imageIds: [first, ...rest],
    })
  }

  if (newContent === slice.content) return slice
  return new Slice(newContent, slice.openStart, slice.openEnd)
}

export default Image
